
import { supabase } from '@/integrations/supabase/client';
import { connectionsService, ConnectionsResponse } from './services/ConnectionsService';
import { chatDataService } from './services/ChatDataService';
import { ProfileData } from '@/types/profile';
import { debugLogger } from '@/utils/debugLogger';
import { presenceService } from './PresenceService';

export interface DashboardData {
  userProfile: any;
  onboardingStatus: any;
  feedProfiles: ProfileData[];
  connectionStats: any;
  notificationCounts: any;
}

export interface ChatListData {
  conversations: any[];
  totalCount: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface Subscriber {
  id: string;
  onDataUpdate: (data: any) => void;
  onError: (error: string) => void;
}

class DataCentralManager {
  private static instance: DataCentralManager;
  private cache = new Map<string, CacheEntry<any>>();
  private subscribers = new Map<string, Subscriber>();
  private userId: string | null = null;
  private connectionStatusCache = new Map<string, string>();
  
  // Add debounce control
  private lastClearAllTime = 0;
  private readonly CLEAR_ALL_DEBOUNCE = 5000; // 5 seconds

  static getInstance(): DataCentralManager {
    if (!DataCentralManager.instance) {
      DataCentralManager.instance = new DataCentralManager();
    }
    return DataCentralManager.instance;
  }

  initialize(userId: string) {
    if (this.userId !== userId) {
      this.userId = userId;
      this.clearAllCache();
      debugLogger.debug('DataCentralManager', `Initialized for user: ${userId}`);
    }
  }

  cleanup() {
    debugLogger.debug('DataCentralManager', 'Cleaning up');
    this.clearAllCache();
    this.subscribers.clear();
    this.userId = null;
  }

  clearConnectionsCache() {
    debugLogger.debug('DataCentralManager', 'Clearing connections cache');
    this.cache.delete('connections');
    // Also clear any related cache that might be affected
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('connections') || key.includes('feed')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  clearProfileRelatedCache() {
    debugLogger.debug('DataCentralManager', 'Clearing profile-related cache');
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('feed') || key.includes('profile')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Public method to clear all cache with debounce protection
  clearAllCache() {
    const now = Date.now();
    
    // Debounce clearAllCache calls to prevent excessive clearing
    if (now - this.lastClearAllTime < this.CLEAR_ALL_DEBOUNCE) {
      debugLogger.debug('DataCentralManager', 'clearAllCache debounced');
      return;
    }
    
    this.lastClearAllTime = now;
    debugLogger.debug('DataCentralManager', 'Clearing all cache (public method)');
    this.cache.clear();
    this.connectionStatusCache.clear();
    
    // Notify all subscribers about the cache clear
    this.notifySubscribers('cache_cleared', { cleared: true });
  }

  private isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private setCache<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry || this.isExpired(entry)) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  private isValidProfileArray(data: any): data is ProfileData[] {
    return Array.isArray(data) && data.every(item => 
      typeof item === 'object' && item !== null && typeof item.id === 'string'
    );
  }

  async getFeedData(sortBy: string = 'distance', forceRefresh: boolean = false): Promise<DashboardData> {
    const cacheKey = `feed_${sortBy}`;
    
    if (!forceRefresh) {
      const cached = this.getCache<DashboardData>(cacheKey);
      if (cached) {
        debugLogger.debug('DataCentralManager', 'Returning cached feed data');
        return cached;
      }
    }

    if (!this.userId) {
      throw new Error('User not initialized');
    }

    try {
      debugLogger.debug('DataCentralManager', `Fetching fresh feed data with forceRefresh: ${forceRefresh}`);
      
      const { data, error } = await supabase.rpc('get_user_dashboard_data', {
        current_user_id: this.userId
      });

      if (error) {
        debugLogger.error('DataCentralManager', 'RPC error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('Nenhum dado retornado do dashboard');
      }

      const result = data[0];
      
      // Safely parse feed_profiles with type checking
      let feedProfiles: ProfileData[] = [];
      if (result.feed_profiles && this.isValidProfileArray(result.feed_profiles)) {
        feedProfiles = result.feed_profiles.map((profile: any) => ({
          ...profile,
          connection_status: profile.connection_status || 'none'
        }));
      }

      const dashboardData: DashboardData = {
        userProfile: result.user_profile || null,
        onboardingStatus: result.onboarding_status || { completed: false, current_step: 0 },
        feedProfiles,
        connectionStats: result.connection_stats || { sent_count: 0, received_count: 0, matched_count: 0 },
        notificationCounts: result.notification_counts || { unread_count: 0, total_count: 0 }
      };

      // Update connection status cache
      feedProfiles.forEach(profile => {
        if (profile.connection_status) {
          this.connectionStatusCache.set(profile.id, profile.connection_status);
        }
      });

      // Use longer TTL for feed data since it's expensive to fetch
      this.setCache(cacheKey, dashboardData, 10 * 60 * 1000); // 10 minutes
      debugLogger.debug('DataCentralManager', 'Feed data cached successfully');
      
      return dashboardData;
    } catch (error) {
      debugLogger.error('DataCentralManager', 'Error fetching feed data:', error);
      throw error;
    }
  }

  async getConnectionsData(userProfile: { role: string; hidden: boolean; onboarding_completed: boolean }, forceRefresh: boolean = false): Promise<ConnectionsResponse> {
    const cacheKey = 'connections';
    
    if (!forceRefresh) {
      const cached = this.getCache<ConnectionsResponse>(cacheKey);
      if (cached) {
        debugLogger.debug('DataCentralManager', 'Returning cached connections data');
        return cached;
      }
    }

    if (!this.userId) {
      throw new Error('User not initialized');
    }

    try {
      debugLogger.debug('DataCentralManager', `Fetching fresh connections data with forceRefresh: ${forceRefresh}`);
      
      const connectionsData = await connectionsService.getConnections(this.userId, userProfile);
      
      // Cache with shorter TTL for connections since they change frequently
      this.setCache(cacheKey, connectionsData, 2 * 60 * 1000); // 2 minutes
      debugLogger.debug('DataCentralManager', 'Connections data cached successfully');
      
      return connectionsData;
    } catch (error) {
      debugLogger.error('DataCentralManager', 'Error fetching connections data:', error);
      throw error;
    }
  }

  async getChatListData(forceRefresh: boolean = false): Promise<ChatListData> {
    const cacheKey = 'chats';
    
    if (!forceRefresh) {
      const cached = this.getCache<ChatListData>(cacheKey);
      if (cached) {
        debugLogger.debug('DataCentralManager', 'Returning cached chat data');
        return cached;
      }
    }

    if (!this.userId) {
      throw new Error('User not initialized');
    }

    try {
      debugLogger.debug('DataCentralManager', 'Fetching fresh chat data');
      
      const chatData = await chatDataService.getChatList(this.userId);
      
      this.setCache(cacheKey, chatData);
      debugLogger.debug('DataCentralManager', 'Chat data cached successfully');
      
      return chatData;
    } catch (error) {
      debugLogger.error('DataCentralManager', 'Error fetching chat data:', error);
      throw error;
    }
  }

  updateConnectionStatus(profileId: string, status: string) {
    this.connectionStatusCache.set(profileId, status);
    // Only invalidate feed cache when connection status changes, not all cache
    const feedKeys = Array.from(this.cache.keys()).filter(key => key.startsWith('feed_'));
    feedKeys.forEach(key => this.cache.delete(key));
    // Also invalidate connections cache
    this.cache.delete('connections');
  }

  getConnectionStatus(profileId: string): string {
    return this.connectionStatusCache.get(profileId) || 'none';
  }

  subscribe(id: string, subscriber: Subscriber): () => void {
    this.subscribers.set(id, subscriber);
    
    return () => {
      this.subscribers.delete(id);
    };
  }

  private notifySubscribers(type: string, data: any) {
    this.subscribers.forEach(subscriber => {
      try {
        subscriber.onDataUpdate({ type, data });
      } catch (error) {
        debugLogger.error('[DataCentralManager] Error notifying subscriber:', error);
        subscriber.onError('Erro ao atualizar dados');
      }
    });
  }

  async updateActivity(activityType: string = 'general') {
    if (!this.userId) return;
    
    try {
      await presenceService.updatePresence(this.userId, activityType);
    } catch (error: any) {
      // Log como warning - activity tracking não deve quebrar fluxo
      debugLogger.warn('[DataCentralManager] Could not update activity:', {
        activityType,
        error: error?.message,
        code: error?.code
      });
    }
  }

  getCacheInfo() {
    return {
      totalEntries: this.cache.size,
      connectionStatusEntries: this.connectionStatusCache.size,
      subscriberCount: this.subscribers.size
    };
  }
}

export const dataCentralManager = DataCentralManager.getInstance();
